// BulletinEditor — the operator's notes on clients' app windows, or their Shop banners.
//
// One component, two placements (app_bulletins.placement, sql/095):
//   window  notes taped to the upper pane of the window on the customer's Home — 4 show at most
//   shop    banners across the top of the Shop screen — 6 show at most
// Rows are org-scoped by RLS (org_isolation), so reads carry no org filter and an insert sets
// organization_id to the operator's own org (the policy's WITH CHECK refuses anything else).
//
// Layout follows the CRM kit: list on the left (the order clients see), the selected row's
// editor on the right with a live preview on top. On a phone the editor replaces the list and
// Back returns to it.
//
// The app shows what the server sends and nothing else — so this page is the only place an
// offer can come from. Its job is to make what clients will see unmistakable: a preview of
// the note, a mock of the whole window, and a plain status on every row.
import { useEffect, useMemo, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";
import { useBackClose } from "../lib/navStack";
import {
  CAPACITY, LIMITS, KINDS, KIND_LABEL, WINDOW_STYLES, SHOP_STYLES, STYLE_LABEL, FASTENERS, COLORS, FONTS, ICONS,
  LINKS, STAMP_IDEAS, blankDraft, draftFrom, validate, ordered, statusOf, shortDate, saveError,
} from "./bulletinDesigns";
import { NotePaper, Fastener, BannerCard, WindowMock, ShopMock, noteLook, describeLook, NAVY } from "./BulletinPreview";

const HJ = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" });

const WORDS = {
  window: { noun: "note", where: "on their window", place: "the window", mock: "The window right now" },
  shop: { noun: "banner", where: "in their Shop", place: "the Shop", mock: "The Shop right now" },
};

export function BulletinEditor({ placement, rows, setRows, org, showToast, onReload, onDirty }) {
  const isMobile = useIsMobile();
  // Below ~1100px the editor is too narrow beside the list, so it takes the list's place instead
  // (the phone layout, with roomier padding).
  const narrow = useIsMobile(1100);
  const W = WORDS[placement];
  const cap = CAPACITY[placement];
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);

  const list = useMemo(() => ordered(rows.filter((r) => (r.placement || "window") === placement)), [rows, placement]);
  const live = list.filter((r) => statusOf(r, now) === "live");
  const shownIds = new Set(live.slice(0, cap).map((r) => r.id));

  const [sel, setSel] = useState(null);          // a row id, "new", or null
  const [copyOf, setCopyOf] = useState(null);    // the title a new draft was duplicated from
  const [draft, setDraft] = useState(null);
  const [base, setBase] = useState("");          // the draft as opened, to tell an edit from a glance
  const [busy, setBusy] = useState(null);        // "save" · "delete" · a row id (switch) · "order"
  const [err, setErr] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [dragId, setDragId] = useState(null);
  const editorRef = useRef(null);
  const [overId, setOverId] = useState(null);

  const dirty = draft != null && JSON.stringify(draft) !== base;
  // ShopPage asks before a section switch would throw these edits away.
  useEffect(() => { onDirty && onDirty(dirty); }, [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const check = draft ? validate(draft, placement, now) : null;
  const preview = draft ? { ...check.row, id: sel === "new" ? "new" : sel } : null;

  const open = (id, d, from = null) => {
    setSel(id); setDraft(d); setBase(JSON.stringify(d)); setCopyOf(from); setErr(null); setShowErrors(false);
  };
  const leaveOk = () => !dirty || window.confirm(`Discard your changes to this ${W.noun}?`);
  const select = (r) => { if (sel === r.id || !leaveOk()) return; open(r.id, draftFrom(r)); };
  const startNew = () => { if (!leaveOk()) return; open("new", blankDraft(placement)); };
  const duplicate = (r) => {
    if (!leaveOk()) return;
    const d = { ...draftFrom(r), title: `Copy of ${r.title}`.slice(0, LIMITS.title), active: false };
    open("new", d, r.title);
    setBase(""); // a copy is unsaved work from the first moment
  };
  const close = () => { setSel(null); setDraft(null); setBase(""); setCopyOf(null); setErr(null); };
  const closeAsked = () => { if (leaveOk()) close(); };
  // A phone shows the editor INSTEAD of the list, so Back has to return to the list.
  useBackClose(narrow && sel != null, close);

  // Where the editor takes the list's place, opening it scrolls to its top — otherwise it opens
  // wherever the row was, mid-form, with its Back button out of sight.
  useEffect(() => {
    if (narrow && sel != null && editorRef.current) editorRef.current.scrollIntoView({ block: "start" });
  }, [narrow, sel]);

  // If the open row disappears (deleted elsewhere, reload), the editor goes with it.
  useEffect(() => { if (sel && sel !== "new" && !list.some((r) => r.id === sel)) close(); }, [list, sel]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  async function request(url, init) {
    const r = await fetch(url, init);
    if (!r.ok) throw new Error((await r.text()) || String(r.status));
    return /return=minimal/.test(init.headers?.Prefer || "") ? null : r.json();
  }

  async function save() {
    if (!draft) return;
    const problems = Object.values(check.errors);
    if (problems.length) { setShowErrors(true); setErr(`${problems.join(" ")} Nothing was saved.`); return; }
    if (!org?.id) { setErr("Your business details haven't loaded yet. Reload the page and try again."); return; }
    setBusy("save"); setErr(null);
    try {
      if (sel === "new") {
        // New rows go to the top — the first thing clients see, like the newest note on a board.
        const top = list.length ? Math.min(...list.map((r) => Number(r.sort) || 0)) - 10 : 0;
        const [created] = await request(`${SUPABASE_URL}/rest/v1/app_bulletins`, {
          method: "POST", headers: { ...HJ(), Prefer: "return=representation" },
          body: JSON.stringify({ ...check.row, organization_id: org.id, sort: top }),
        });
        setRows((rs) => [...rs, created]);
        open(created.id, draftFrom(created));
        showToast && showToast(created.active ? `Posted — clients see it ${W.where} next time they open the app.` : "Saved as a draft. Switch it on when you're ready.");
      } else {
        const [updated] = await request(`${SUPABASE_URL}/rest/v1/app_bulletins?id=eq.${sel}`, {
          method: "PATCH", headers: { ...HJ(), Prefer: "return=representation" }, body: JSON.stringify(check.row),
        });
        if (!updated) throw new Error("not_found");
        setRows((rs) => rs.map((x) => (x.id === sel ? updated : x)));
        open(updated.id, draftFrom(updated));
        showToast && showToast("Saved.");
      }
    } catch (e) {
      const m = /not_found/.test(e.message) ? `That ${W.noun} isn't there any more — reload the page.` : saveError(e.message);
      setErr(m); showToast && showToast(m, "error");
    }
    setBusy(null);
  }

  async function remove() {
    const r = list.find((x) => x.id === sel);
    if (!r || !window.confirm(`Delete “${r.title}”? Clients stop seeing it right away. This can't be undone.`)) return;
    setBusy("delete"); setErr(null);
    try {
      // RLS-filtered deletes answer 200 with nothing removed — only a returned row counts.
      const gone = await request(`${SUPABASE_URL}/rest/v1/app_bulletins?id=eq.${r.id}`, { method: "DELETE", headers: { ...HJ(), Prefer: "return=representation" } });
      if (!Array.isArray(gone) || !gone.length) throw new Error("not_found");
      setRows((rs) => rs.filter((x) => x.id !== r.id));
      close();
      showToast && showToast("Deleted.");
    } catch (e) {
      const m = /not_found/.test(e.message) ? `Couldn't delete that ${W.noun} — reload the page and check it's still there.` : saveError(e.message);
      setErr(m); showToast && showToast(m, "error");
    }
    setBusy(null);
  }

  async function toggle(r) {
    setBusy(r.id);
    try {
      const [updated] = await request(`${SUPABASE_URL}/rest/v1/app_bulletins?id=eq.${r.id}`, {
        method: "PATCH", headers: { ...HJ(), Prefer: "return=representation" }, body: JSON.stringify({ active: !r.active }),
      });
      if (!updated) throw new Error("not_found");
      setRows((rs) => rs.map((x) => (x.id === r.id ? updated : x)));
      // The open editor follows the switch — unless it holds unsaved edits, which it keeps.
      if (sel === r.id && !dirty) open(updated.id, draftFrom(updated));
      else if (sel === r.id) { setDraft((d) => ({ ...d, active: updated.active })); setBase((b) => { try { return JSON.stringify({ ...JSON.parse(b), active: updated.active }); } catch { return b; } }); }
      showToast && showToast(updated.active ? "Switched on." : `Switched off — clients no longer see it.`);
    } catch (e) { showToast && showToast(saveError(e.message), "error"); }
    setBusy(null);
  }

  // Reorder = renumber. Several rows can share a sort value (the default is 0), so a swap
  // can't just trade two numbers; every row gets its place × 10 and only changed rows are sent.
  async function reorder(ids) {
    const want = new Map(ids.map((id, i) => [id, i * 10]));
    const changed = list.filter((r) => want.get(r.id) !== Number(r.sort));
    if (!changed.length) return;
    setBusy("order");
    setRows((rs) => rs.map((x) => (want.has(x.id) ? { ...x, sort: want.get(x.id) } : x)));
    try {
      await Promise.all(changed.map((r) => request(`${SUPABASE_URL}/rest/v1/app_bulletins?id=eq.${r.id}`, {
        method: "PATCH", headers: { ...HJ(), Prefer: "return=minimal" }, body: JSON.stringify({ sort: want.get(r.id) }),
      })));
    } catch (e) {
      showToast && showToast("Couldn't save the new order — reloading what's saved.", "error");
      onReload && onReload();
    }
    setBusy(null);
  }
  const move = (id, dir) => {
    const ids = list.map((r) => r.id);
    const i = ids.indexOf(id), j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    reorder(ids);
  };
  const dropOn = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const ids = list.map((r) => r.id).filter((id) => id !== dragId);
    ids.splice(ids.indexOf(targetId), 0, dragId);
    reorder(ids);
  };

  // ── pieces ─────────────────────────────────────────────────────────

  const statusPill = (r) => {
    const st = statusOf(r, now);
    if (st === "live" && !shownIds.has(r.id)) return <span className="badge" style={{ background: "#FEF6E7", color: "#8A5A06", border: "1px solid #F6D88A" }}>Live · not shown</span>;
    if (st === "live") return <span className="badge badge-green">Live</span>;
    if (st === "scheduled") return <span className="badge badge-blue">Scheduled</span>;
    if (st === "ended") return <span className="badge badge-gray">Ended</span>;
    return <span className="badge badge-gray">Off</span>;
  };
  const when = (r) => {
    const st = statusOf(r, now);
    if (st === "scheduled") return `from ${shortDate(r.starts_at)}${r.ends_at ? ` to ${shortDate(r.ends_at)}` : ""}`;
    if (st === "ended") return `ended ${shortDate(r.ends_at)}`;
    if (r.ends_at) return `until ${shortDate(r.ends_at)}`;
    return null;
  };

  const overCap = live.length > cap;
  const mockRows = live.slice(0, cap);

  const listPane = (
    <div style={{ display: "grid", gap: 14, minWidth: 0 }}>
      <div className="card" style={{ padding: isMobile ? 14 : 18 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{W.mock}</div>
          <div style={{ fontSize: 12, color: "var(--text2)" }}>{mockRows.length} of {cap} spots · preview</div>
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          {placement === "shop" ? <ShopMock banners={mockRows} orgName={org?.name} /> : <WindowMock notes={mockRows} />}
        </div>
      </div>

      {overCap ? (
        <div role="status" style={{ background: "#FEF6E7", border: "1px solid #F6D88A", color: "#8A5A06", borderRadius: "var(--r)", padding: "11px 14px", fontSize: 13, lineHeight: 1.45 }}>
          <strong>{live.length} {W.noun}s are live, but {W.place} holds {cap}.</strong> Clients see the first {cap} in this list — switch one off or move it down.
        </div>
      ) : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 16px", borderBottom: list.length ? "1px solid var(--border)" : "none" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{placement === "shop" ? "Banners" : "Notes"} <span style={{ color: "var(--text2)", fontWeight: 500 }}>· {list.length}</span></div>
            <div style={{ fontSize: 12, color: "var(--text2)" }}>In the order clients see them{isMobile ? "" : " — drag or use the arrows"}.</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={startNew}>+ New {W.noun}</button>
        </div>

        {list.length === 0 ? (
          <div style={{ padding: "26px 18px 28px", textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{placement === "shop" ? "No banners yet" : "Nothing on the window yet"}</div>
            <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 6, lineHeight: 1.5, maxWidth: 380, marginInline: "auto" }}>
              {placement === "shop"
                ? "Put a deal, a local shop or something coming soon across the top of clients' Shop. It shows the next time they open the app — no app update needed."
                : "Tape a note to every client's window — a deal, a hello, a reminder. It shows the next time they open the app — no app update needed."}
            </div>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={startNew}>+ New {W.noun}</button>
          </div>
        ) : list.map((r, i) => {
          const on = sel === r.id;
          const sub = [KIND_LABEL[r.kind] || r.kind, r.style ? STYLE_LABEL[r.style] || r.style : (placement === "shop" ? null : "app's paper"), when(r)].filter(Boolean).join(" · ");
          return (
            <div key={r.id}
                 draggable={!isMobile}
                 onDragStart={(e) => { setDragId(r.id); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", r.id); } catch { /* old browsers */ } }}
                 onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverId(r.id); } }}
                 onDragLeave={() => setOverId((o) => (o === r.id ? null : o))}
                 onDrop={(e) => { e.preventDefault(); dropOn(r.id); setDragId(null); setOverId(null); }}
                 onDragEnd={() => { setDragId(null); setOverId(null); }}
                 style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", cursor: "pointer",
                   borderTop: i ? "1px solid var(--border)" : "none",
                   boxShadow: overId === r.id && dragId !== r.id ? "inset 0 2px 0 var(--accent)" : "none",
                   background: on ? "var(--accent-weak)" : "transparent", opacity: dragId === r.id ? .5 : 1 }}
                 onClick={() => select(r)}
                 role="button" tabIndex={0} aria-pressed={on}
                 onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); select(r); } }}>
              <Thumb r={r} placement={placement} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</div>
                <div style={{ fontSize: 12, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>
                <div style={{ marginTop: 4 }}>{statusPill(r)}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }} onClick={(e) => e.stopPropagation()}>
                <IconBtn label={`Move “${r.title}” up`} disabled={i === 0 || busy === "order"} onClick={() => move(r.id, -1)}>▲</IconBtn>
                <IconBtn label={`Move “${r.title}” down`} disabled={i === list.length - 1 || busy === "order"} onClick={() => move(r.id, 1)}>▼</IconBtn>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <Switch on={!!r.active} busy={busy === r.id} label={`Show “${r.title}” to clients`} onClick={() => toggle(r)} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const editorPane = draft ? (
    <div ref={editorRef} className="card" style={{ padding: 0, overflow: "hidden", minWidth: 0, scrollMarginTop: 76 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ minWidth: 0 }}>
          {narrow ? <button className="btn btn-ghost btn-sm" style={{ marginBottom: 8 }} onClick={closeAsked}>‹ All {W.noun}s</button> : null}
          <div style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-.01em" }}>
            {sel === "new" ? (copyOf ? `Copy of “${copyOf}”` : `New ${W.noun}`) : `Edit ${W.noun}`}
          </div>
          <div style={{ fontSize: 12, color: "var(--text2)" }}>
            {sel === "new" ? "Not saved yet." : dirty ? "Unsaved changes." : "Saved."}
          </div>
        </div>
        {!narrow ? <button className="close-btn" aria-label="Close the editor" onClick={closeAsked}>✕</button> : null}
      </div>

      {/* The stage: the note (or banner) as it'll look, big enough to judge. */}
      <div style={{ padding: isMobile ? "26px 14px" : "34px 20px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 220,
        background: placement === "shop"
          ? "radial-gradient(120% 70% at 20% 0%, #EAF1FB, transparent), linear-gradient(180deg, #E4ECF7, #F3F6FB)"
          : "radial-gradient(70% 40% at 20% 92%, rgba(80,125,74,.55), transparent 70%), radial-gradient(60% 40% at 80% 95%, rgba(93,140,82,.5), transparent 70%), linear-gradient(180deg, #8EC1EC, #D4E7F7)" }}
           role="img" aria-label={`Preview: ${describeLook(preview, placement)}. ${preview.title || ""}`}>
        {placement === "shop"
          ? <div style={{ width: "100%", maxWidth: preview.style === "tile" ? 180 : 330 }}><BannerCard banner={preview} /></div>
          : <NotePaper note={preview} s={isMobile ? 1 : 1.1} showLink />}
      </div>
      <div style={{ fontSize: 11.5, color: "var(--text2)", padding: "8px 16px", borderBottom: "1px solid var(--border)", background: "var(--bg)" }}>
        Preview — close to what the app draws, not pixel-exact.{placement === "window" && (!preview.style || !preview.fastener || preview.tilt == null) ? " Anything left on “App's pick” is chosen by the app, so it may differ." : ""}
      </div>

      <div style={{ padding: isMobile ? "4px 14px 16px" : "6px 20px 20px" }}>
        <Section title="Words" first>
          <Field label="Title" count={draft.title.trim().length} max={LIMITS.title} error={showErrors && check.errors.title}>
            <input className="input" value={draft.title} maxLength={LIMITS.title + 20} placeholder={placement === "shop" ? "e.g. Grooming week" : "e.g. Shop local!"}
                   onChange={(e) => set("title", e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Message" hint="Optional — a line or two." count={draft.body.trim().length} max={LIMITS.body} error={showErrors && check.errors.body}>
            <textarea className="input" rows={2} value={draft.body} maxLength={LIMITS.body + 40}
                      onChange={(e) => set("body", e.target.value)} style={{ ...inputStyle, minHeight: 64 }} />
          </Field>
          <Field label="What it is" hint={placement === "window" ? "Helps the app pick a look when you leave one on “App's pick”." : null}>
            <select className="input" value={draft.kind} onChange={(e) => set("kind", e.target.value)} style={inputStyle}>
              {KINDS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
        </Section>

        <Section title="What a tap does">
          <div className="seg" role="radiogroup" aria-label="Button" style={{ marginBottom: 10 }}>
            {[["none", "No button"], ["claim", "Claim button"]].map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={draft.action === k} className={draft.action === k ? "on" : ""}
                      onClick={() => set("action", k)} style={isMobile ? { padding: "9px 13px", minHeight: 40 } : undefined}>{l}</button>
            ))}
          </div>
          {draft.action === "claim" ? (
            <Field label="Button words" hint={`Clients tap it and a message lands in your Requests tab: “I'd like to claim: ${check.row.title || "…"}”. Blank = “Claim it”.`}
                   count={draft.cta.trim().length} max={LIMITS.cta} error={showErrors && check.errors.cta}>
              <input className="input" value={draft.cta} maxLength={LIMITS.cta + 10} placeholder="Claim it" onChange={(e) => set("cta", e.target.value)} style={inputStyle} />
            </Field>
          ) : null}
          <Field label="Tapping it opens" error={showErrors && check.errors.link}>
            <select className="input" value={draft.linkKind} onChange={(e) => set("linkKind", e.target.value)} style={inputStyle}>
              {LINKS.map(([k, l]) => <option key={k} value={k}>{k && k !== "web" ? `${l} (in the app)` : l}</option>)}
            </select>
            {draft.linkKind === "web" ? (
              <input className="input" type="url" value={draft.linkUrl} placeholder="https://" onChange={(e) => set("linkUrl", e.target.value)}
                     style={{ ...inputStyle, marginTop: 8 }} aria-label="Web page address" />
            ) : null}
          </Field>
        </Section>

        <Section title="The look" action={<button type="button" className="btn btn-ghost btn-xs" onClick={() => setDraft((d) => shuffle(d, placement))}>🎲 Shuffle</button>}>
          <Picker label={placement === "shop" ? "Banner" : "Paper"} error={showErrors && check.errors.style} min={placement === "shop" ? 140 : 78}>
            {placement === "window" ? <Tile on={!draft.style} label="App's pick" onClick={() => set("style", "")}><span style={{ fontSize: 20 }}>✨</span></Tile> : null}
            {(placement === "shop" ? SHOP_STYLES : WINDOW_STYLES).map(([k, l]) => (
              <Tile key={k} on={draft.style === k} label={l} onClick={() => set("style", k)}>
                <StyleSwatch placement={placement} row={{ ...check.row, style: k }} />
              </Tile>
            ))}
          </Picker>

          {placement === "window" ? (
            <Picker label="Held up by">
              <Tile on={!draft.fastener} label="App's pick" onClick={() => set("fastener", "")}><span style={{ fontSize: 20 }}>✨</span></Tile>
              {FASTENERS.map(([k, l]) => (
                <Tile key={k} on={draft.fastener === k} label={l} onClick={() => set("fastener", k)}>
                  <div style={{ width: 40, height: 34, marginTop: 8, background: "#FBFAF6", boxShadow: "0 1px 2px rgba(0,0,0,.25)", position: "relative" }}>
                    <Fastener kind={k} s={0.55} />
                  </div>
                </Tile>
              ))}
            </Picker>
          ) : null}

          <Picker label="Color" compact>
            <Swatch on={!draft.color} label={placement === "shop" ? "The banner's own" : "The paper's own"} onClick={() => set("color", "")} fill="linear-gradient(135deg,#fff 0 48%,#ddd 48% 52%,#fff 52%)" ink="#666" />
            {COLORS.map(([k, l, paper, ink]) => <Swatch key={k} on={draft.color === k} label={l} onClick={() => set("color", k)} fill={paper} ink={ink} />)}
          </Picker>

          <Picker label="Handwriting" compact>
            <Chip on={!draft.font} onClick={() => set("font", "")}>App's pick</Chip>
            {FONTS.map(([k, l, title, , weight]) => (
              <Chip key={k} on={draft.font === k} onClick={() => set("font", k)}><span style={{ fontFamily: title, fontWeight: weight, fontSize: 15 }}>Aa</span> {l}</Chip>
            ))}
          </Picker>

          <Picker label="Picture" compact>
            <Chip on={!draft.icon} onClick={() => set("icon", "")}>None</Chip>
            {ICONS.map(([k, glyph]) => (
              <button key={k} type="button" title={k} aria-label={k} aria-pressed={draft.icon === k} onClick={() => set("icon", k)}
                      style={{ ...iconBtnStyle, ...(draft.icon === k ? onRing : null) }}>{glyph}</button>
            ))}
          </Picker>

          <Field label="Stamp" hint="A short word stamped on it." count={draft.stamp.trim().length} max={LIMITS.stamp} error={showErrors && check.errors.stamp}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <input className="input" value={draft.stamp} maxLength={LIMITS.stamp + 6} placeholder="e.g. NEW" onChange={(e) => set("stamp", e.target.value)} style={{ ...inputStyle, width: 150, flex: "0 1 150px" }} />
              {STAMP_IDEAS.map((w) => <Chip key={w} on={draft.stamp.trim() === w} onClick={() => set("stamp", draft.stamp.trim() === w ? "" : w)}>{w}</Chip>)}
            </div>
          </Field>

          {placement === "window" ? (
            <Field label="Tilt">
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text)", cursor: "pointer", minHeight: 32 }}>
                <input type="checkbox" checked={draft.tilt == null} onChange={(e) => set("tilt", e.target.checked ? null : Math.round(noteLook(preview).tilt))} />
                Let the app tilt it
              </label>
              {draft.tilt != null ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input type="range" min={LIMITS.tiltMin} max={LIMITS.tiltMax} step={1} value={draft.tilt} aria-label="Tilt in degrees"
                         onChange={(e) => set("tilt", Number(e.target.value))} style={{ flex: 1, padding: 0, border: "none", boxShadow: "none" }} />
                  <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 13, width: 40, textAlign: "right" }}>{draft.tilt > 0 ? `${draft.tilt}°` : draft.tilt < 0 ? `−${-draft.tilt}°` : "0°"}</span>
                </div>
              ) : null}
            </Field>
          ) : null}
        </Section>

        <Section title="When">
          <button type="button" role="switch" aria-checked={draft.active} onClick={() => set("active", !draft.active)}
                  style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, width: "100%", minHeight: 44, padding: 0, border: "none", background: "transparent", font: "inherit", textAlign: "left", cursor: "pointer", marginBottom: 6 }}>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text)" }}>Show it to clients</span>
              <span style={{ display: "block", fontSize: 12, color: "var(--text2)" }}>{draft.active ? "On — between the dates below." : "Off — saved as a draft, nobody sees it."}</span>
            </span>
            <SwitchTrack on={draft.active} />
          </button>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 10 }}>
            <Field label="Starts" hint="Blank = right away." error={showErrors && check.errors.starts}>
              <DateInput value={draft.starts} onChange={(v) => set("starts", v)} label="Start date and time" />
            </Field>
            <Field label="Ends" hint="Blank = until you take it down." error={(showErrors || draft.ends) && check.errors.ends}>
              <DateInput value={draft.ends} onChange={(v) => set("ends", v)} label="End date and time" />
            </Field>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 4 }}>Times are in this computer's time zone.</div>
        </Section>

        {check.warnings.length ? (
          <div style={{ display: "grid", gap: 6, marginTop: 14 }}>
            {check.warnings.map((w) => (
              <div key={w} style={{ background: "#FEF6E7", border: "1px solid #F6D88A", color: "#8A5A06", borderRadius: 10, padding: "9px 12px", fontSize: 12.5, lineHeight: 1.45 }}>{w}</div>
            ))}
          </div>
        ) : null}
        {err ? <div role="alert" className="alert alert-error" style={{ marginTop: 14, marginBottom: 0 }}>{err}</div> : null}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 16 }}>
          <button className="btn btn-primary" disabled={!!busy} onClick={save}>
            {busy === "save" ? "Saving…" : sel === "new" ? (draft.active ? `Post ${W.noun}` : "Save as draft") : "Save changes"}
          </button>
          <button className="btn btn-ghost" disabled={!!busy} onClick={closeAsked}>Cancel</button>
          {sel !== "new" ? (
            <>
              <span style={{ flex: 1 }} />
              <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => duplicate(list.find((x) => x.id === sel))}>Duplicate</button>
              <button className="btn btn-danger btn-sm" disabled={!!busy} onClick={remove}>{busy === "delete" ? "Deleting…" : "Delete"}</button>
            </>
          ) : null}
        </div>
      </div>
    </div>
  ) : (
    <div className="card" style={{ padding: "30px 24px", textAlign: "center" }}>
      <div style={{ fontSize: 15, fontWeight: 600 }}>{list.length ? `Pick a ${W.noun} to edit it` : `Your first ${W.noun}`}</div>
      <div style={{ fontSize: 13, color: "var(--text2)", marginTop: 6, lineHeight: 1.5, maxWidth: 400, marginInline: "auto" }}>
        Choose the words, the {placement === "shop" ? "banner" : "paper and what holds it up"}, a color, handwriting, a picture and a stamp — and see it here before clients do.
      </div>
      <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={startNew}>+ New {W.noun}</button>
    </div>
  );

  if (narrow) return <div style={{ maxWidth: 760 }}>{sel != null ? editorPane : listPane}</div>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
      {listPane}
      <div style={{ position: "sticky", top: 76, maxHeight: "calc(100vh - 92px)", overflowY: "auto", borderRadius: "var(--r-lg)" }}>{editorPane}</div>
    </div>
  );
}

// ── small parts ─────────────────────────────────────────────────────

const inputStyle = { width: "100%", marginTop: 4, boxSizing: "border-box" };
const onRing = { boxShadow: "0 0 0 2px var(--accent)", background: "var(--accent-weak)" };
const iconBtnStyle = { width: 38, height: 38, borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)", fontSize: 18, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, color: NAVY, fontWeight: 800 };

function Section({ title, action, first, children }) {
  return (
    <div style={{ paddingTop: 16, marginTop: first ? 0 : 4, borderTop: first ? "none" : "1px solid var(--border)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text2)" }}>{title}</div>
        {action}
      </div>
      <div style={{ display: "grid", gap: 12 }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, count, max, error, children }) {
  const over = max != null && count > max;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text2)" }}>{label}</span>
        {max != null ? <span style={{ fontSize: 11.5, color: over ? "var(--danger)" : "var(--text3)", fontVariantNumeric: "tabular-nums", fontWeight: over ? 600 : 400 }}>{count}/{max}</span> : null}
      </div>
      {children}
      {error ? <div role="alert" style={{ fontSize: 12, color: "var(--danger)", marginTop: 4 }}>{error}</div>
        : hint ? <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 4, lineHeight: 1.45 }}>{hint}</div> : null}
    </div>
  );
}

// A banner tile needs a wider column than a paper; spanning two narrow columns left a phone
// with one tile per row and an empty third column.
function Picker({ label, compact, error, min = 78, children }) {
  return (
    <div role="group" aria-label={label}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text2)", marginBottom: 6 }}>{label}</div>
      <div style={compact ? { display: "flex", flexWrap: "wrap", gap: 6 } : { display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 8 }}>{children}</div>
      {error ? <div role="alert" style={{ fontSize: 12, color: "var(--danger)", marginTop: 4 }}>{error}</div> : null}
    </div>
  );
}

function Tile({ on, label, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} title={label}
            style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", gap: 4, minHeight: 92, padding: "6px 4px 6px",
              border: "1px solid var(--border)", borderRadius: 12, background: on ? "var(--accent-weak)" : "var(--surface)", cursor: "pointer", font: "inherit", overflow: "hidden",
              boxShadow: on ? "0 0 0 2px var(--accent)" : "none" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", overflow: "hidden", pointerEvents: "none" }} aria-hidden="true">{children}</div>
      <span style={{ fontSize: 11, color: on ? "var(--accent)" : "var(--text)", fontWeight: on ? 600 : 500, lineHeight: 1.15, textAlign: "center" }}>{label}</span>
    </button>
  );
}

function Swatch({ on, label, fill, ink, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} aria-label={label} title={label}
            style={{ width: 38, height: 38, borderRadius: "50%", border: "1px solid rgba(0,0,0,.14)", background: fill, color: ink, cursor: "pointer", padding: 0,
              fontWeight: 700, fontSize: 12, boxShadow: on ? "0 0 0 2px var(--surface), 0 0 0 4px var(--accent)" : "none" }}>Aa</button>
  );
}

function Chip({ on, onClick, children }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 34, padding: "5px 11px", borderRadius: 999, border: "1px solid var(--border)", font: "inherit", fontSize: 12.5, cursor: "pointer",
              background: on ? "var(--accent-weak)" : "var(--surface)", color: on ? "var(--accent)" : "var(--text)", fontWeight: on ? 600 : 500, boxShadow: on ? "0 0 0 1.5px var(--accent)" : "none" }}>{children}</button>
  );
}

function IconBtn({ label, disabled, onClick, children }) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled} onClick={onClick}
            style={{ width: 30, height: 24, borderRadius: 6, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text2)", fontSize: 9, cursor: disabled ? "default" : "pointer", opacity: disabled ? .35 : 1, padding: 0 }}>{children}</button>
  );
}

function SwitchTrack({ on }) {
  return (
    <span aria-hidden="true" style={{ width: 44, height: 26, flexShrink: 0, borderRadius: 13, background: on ? "var(--ok)" : "rgba(0,0,0,.14)", position: "relative", transition: "background .2s", display: "inline-block" }}>
      <span style={{ width: 22, height: 22, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: on ? 20 : 2, transition: "left .2s", boxShadow: "0 1px 2px rgba(0,0,0,.25)" }} />
    </span>
  );
}

function Switch({ on, busy, label, onClick }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={busy} onClick={onClick}
            style={{ border: "none", background: "transparent", padding: "9px 2px", cursor: busy ? "wait" : "pointer", opacity: busy ? .5 : 1, display: "flex" }}>
      <SwitchTrack on={on} />
    </button>
  );
}

function DateInput({ value, onChange, label }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
      <input className="input" type="datetime-local" value={value} aria-label={label} onChange={(e) => onChange(e.target.value)} style={{ flex: 1, minWidth: 0, boxSizing: "border-box" }} />
      {value ? <button type="button" className="btn btn-ghost btn-xs" onClick={() => onChange("")} aria-label={`Clear ${label.toLowerCase()}`}>Clear</button> : null}
    </div>
  );
}

// A row's look in a 64-px square for the list.
function Thumb({ r, placement }) {
  if (placement === "shop") {
    return (
      <div aria-hidden="true" style={{ width: 64, height: 46, flexShrink: 0, overflow: "hidden", borderRadius: 9, background: "linear-gradient(180deg,#E4ECF7,#F3F6FB)", position: "relative" }}>
        <div style={{ position: "absolute", left: 3, top: 3, width: 290, transform: "scale(.2)", transformOrigin: "top left" }}><BannerCard banner={r} /></div>
      </div>
    );
  }
  const s = 46 / noteLook(r).w;
  return (
    <div aria-hidden="true" style={{ width: 64, height: 58, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, overflow: "hidden", background: "linear-gradient(180deg,#B9D8F2,#DCEAF7)" }}>
      <NotePaper note={{ ...r, body: null, action: null, stamp: null }} s={s} still />
    </div>
  );
}

// One look option, drawn with the draft's own colour, handwriting and picture.
function StyleSwatch({ placement, row }) {
  if (placement === "shop") {
    return (
      <div style={{ width: 132, height: 52, overflow: "hidden", position: "relative" }}>
        <div style={{ position: "absolute", left: 0, top: 0, width: 300, transform: "scale(.44)", transformOrigin: "top left" }}>
          <BannerCard banner={{ ...row, body: null, action: null, title: row.title || "Aa" }} />
        </div>
      </div>
    );
  }
  const s = 52 / noteLook(row).w;
  return <NotePaper note={{ ...row, title: "Aa", body: null, action: null, stamp: null, fastener: "none", tilt: 0 }} s={s} still />;
}

// A random look — the fastest way to see what's possible.
function shuffle(d, placement) {
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)][0];
  const next = { ...d, style: pick(placement === "shop" ? SHOP_STYLES : WINDOW_STYLES), color: Math.random() < .35 ? "" : pick(COLORS),
    font: Math.random() < .3 ? "" : pick(FONTS), icon: Math.random() < .25 ? "" : pick(ICONS) };
  if (placement === "window") { next.fastener = pick(FASTENERS); next.tilt = Math.round(Math.random() * 16 - 8); }
  return next;
}
